import { TextField } from '../primitives/index.js'
import { Typography } from '../primitives/index.js'
import { FolderOpen, Search } from '@tunasong/icons'
import type { Entity, Persisted } from '@tunasong/schemas'
import React, { useEffect, useState } from 'react'
import { useFavorites } from '../favorites/favorites.hook.js'
import type { NavigationOptions } from '../navigation/navigate.js'
import { EntityListItem } from './entity-list-item.js'
import { ListSkeleton } from './list-skeleton.js'
import { MenuList, type MenuListProps } from '../primitives/index.js'
import styles from './entity-list.module.css'

export interface EntityListProps extends MenuListProps {
  className?: string
  ref?: React.Ref<HTMLUListElement>
  entities: Persisted<Entity>[]
  selected?: Persisted<Entity>[]
  controls?: boolean
  size?: 'tiny' | 'small' | 'large' | 'medium'
  placeholder?: string
  actions?: boolean
  showBreadcrumbs?: boolean | 'inactiveLinks'
  /** Show loading skeleton */
  loading?: boolean
  /** Show search input */
  searchable?: boolean

  onOpen?(entity: Persisted<Entity>, options: NavigationOptions): void
}

export const EntityList = (props: EntityListProps) => {
  const {
    className,
    ref,
    entities,
    actions,
    controls: _controls,
    selected = [],
    size = 'small',
    showBreadcrumbs = false,
    loading = false,
    searchable = false,
    onOpen,
    ...restProps
  } = props

  const [searchQuery, setSearchQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)

  useEffect(() => {
    setFocusedIndex(-1)
  }, [searchQuery])

  const handleOpen = (entity: Persisted<Entity>) => (ev: React.MouseEvent<HTMLLIElement>) => {
    if (!onOpen) {
      return
    }
    ev.preventDefault()
    ev.stopPropagation()
    const navigationMode = ev.shiftKey ? 'dialog' : 'page'
    onOpen(entity, { navigationMode })
  }

  const { isFavorite } = useFavorites()

  // Filter out trashed items and sort favorites to the top
  const items = entities
    .filter(e => !e.trash)
    .filter(e => {
      if (!searchQuery) {
        return true
      }
      const query = searchQuery.toLowerCase()
      return (
        e.name?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query) ||
        e.type.toLowerCase().includes(query)
      )
    })
    .sort((a, b) => {
      const aFav = isFavorite(a)
      const bFav = isFavorite(b)
      if (aFav && !bFav) {
        return -1
      }
      if (!aFav && bFav) {
        return 1
      }
      return 0
    })

  const selectedIds = new Set(selected.map(e => e.id))

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (items.length === 0) {
      return
    }

    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault()
        setFocusedIndex(prev => (prev < items.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        ev.preventDefault()
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : items.length - 1))
        break
      case 'Enter':
        ev.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < items.length && onOpen) {
          const navigationMode = ev.shiftKey ? 'dialog' : 'page'
          onOpen(items[focusedIndex], { navigationMode })
        }
        break
      case 'Home':
        ev.preventDefault()
        setFocusedIndex(0)
        break
      case 'End':
        ev.preventDefault()
        setFocusedIndex(items.length - 1)
        break
    }
  }

  if (loading) {
    return <ListSkeleton numItems={5} />
  }

  const emptyState = (
    <div className={styles.emptyState}>
      <FolderOpen size={48} className={styles.emptyIcon} />
      <Typography variant="body2">{restProps.placeholder ?? 'No items found'}</Typography>
      {searchQuery && (
        <Typography variant="caption" className={styles.emptyHint}>
          Try a different search term
        </Typography>
      )}
    </div>
  )

  return (
    <div className={styles.root}>
      {searchable && (
        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search size={18} className={styles.searchIcon} />,
          }}
          className={styles.searchField}
        />
      )}
      <MenuList
        ref={ref}
        className={className}
        component={'div'}
        onKeyDown={handleKeyDown}
        {...restProps}
      >
        {items.length === 0
          ? emptyState
          : items.map((element, index) => (
              <EntityListItem
                key={element.id}
                size={size}
                favorite={isFavorite(element)}
                actions={actions}
                selected={selectedIds.has(element.id) || index === focusedIndex}
                element={element}
                showBreadcrumbs={showBreadcrumbs}
                onClick={handleOpen(element)}
              />
            ))}
      </MenuList>
    </div>
  )
}
